import { useCallback, useEffect, useMemo, useState } from "react";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { format, parseISO } from "date-fns";

import ConfirmDialog from "components/ConfirmDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController, setMessage } from "context";
import type { ICupomLoja } from "interfaces/loja";
import { deleteCupomLoja, getAllCuponsLoja, updateCupomLoja } from "services/lojaCupom";
import { LojaDataGrid, LojaPageContainer } from "../common";
import CupomModal from "./CupomModal";
import {
  CUPONS_LOJA_ACCENT,
  actionIconSx,
  cuponsLojaToolbarSx,
  emptyStateSx,
  fieldSx,
  gridSx,
  saveButtonSx,
  statusToggleSx,
  switchSx,
} from "./cupons-loja.theme";
import {
  STATUS_OPTIONS,
  TIPO_LABELS,
  VALIDADE_OPTIONS,
  computeStats,
  countVinculos,
  formatValorCupom,
  getValidadeStatus,
  isCupomGlobal,
  matchesFilters,
  validadeChipSx,
  type StatusFilter,
  type ValidadeFilter,
} from "./components/cupons-loja.helpers";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? CUPONS_LOJA_ACCENT : "text.primary"}
        sx={{ fontSize: { xs: "1.25rem", sm: "2rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function formatPeriodo(cupom: ICupomLoja): string {
  const ini = cupom.inicioEm
    ? format(parseISO(String(cupom.inicioEm).slice(0, 10)), "dd/MM/yy")
    : "";
  const fim = cupom.fimEm
    ? format(parseISO(String(cupom.fimEm).slice(0, 10)), "dd/MM/yy")
    : "";
  if (!ini && !fim) return "Sem limite";
  return `${ini || "…"} — ${fim || "…"}`;
}

export default function LojaCupons() {
  const [, dispatch] = useMaterialUIController();
  const [rows, setRows] = useState<ICupomLoja[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [validadeFilter, setValidadeFilter] = useState<ValidadeFilter>("");
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<ICupomLoja | null>(null);
  const [openDelete, setOpenDelete] = useState<{
    open: boolean;
    id?: number;
    codigo?: string;
  }>({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllCuponsLoja();
      setRows(result.data ?? []);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Falha ao carregar cupons.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => matchesFilters(r, search, statusFilter, validadeFilter)),
    [rows, search, statusFilter, validadeFilter],
  );

  const isFiltered = Boolean(search.trim() || statusFilter || validadeFilter);

  const openCreate = useCallback(() => {
    setEditing(null);
    setOpenForm(true);
  }, []);

  const openEdit = useCallback((row: ICupomLoja) => {
    setEditing(row);
    setOpenForm(true);
  }, []);

  const toggleAtivo = useCallback(
    async (row: ICupomLoja) => {
      setTogglingId(row.id);
      try {
        const novo = !row.ativo;
        setRows((prev) =>
          prev.map((c) => (c.id === row.id ? { ...c, ativo: novo } : c)),
        );
        await updateCupomLoja(String(row.id), { ativo: novo });
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: novo ? "Cupom ativado." : "Cupom desativado.",
        });
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : "Falha ao atualizar status.";
        setMessage(dispatch, { open: true, severity: "error", text: msg });
        await load();
      } finally {
        setTogglingId(null);
      }
    },
    [dispatch, load],
  );

  const onConfirmDelete = useCallback(async () => {
    if (!openDelete.id) return;
    try {
      await deleteCupomLoja(String(openDelete.id));
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Cupom excluído.",
      });
      setOpenDelete({ open: false });
      await load();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Falha ao excluir cupom.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
    }
  }, [openDelete.id, dispatch, load]);

  const columns: GridColDef<ICupomLoja>[] = useMemo(
    () => [
      {
        field: "codigo",
        headerName: "Código",
        width: 150,
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => (
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ fontFamily: "monospace", color: CUPONS_LOJA_ACCENT }}
          >
            {params.row.codigo}
          </MDTypography>
        ),
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 130,
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => (
          <Chip
            size="small"
            label={TIPO_LABELS[params.row.tipo]}
            sx={{
              bgcolor: "rgba(249, 96, 48, 0.1)",
              color: CUPONS_LOJA_ACCENT,
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        field: "valor",
        headerName: "Desconto",
        width: 120,
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => (
          <MDTypography variant="button" fontWeight="medium">
            {formatValorCupom(params.row)}
          </MDTypography>
        ),
      },
      {
        field: "periodo",
        headerName: "Período",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => (
          <MDTypography variant="caption" color="text.secondary">
            {formatPeriodo(params.row)}
          </MDTypography>
        ),
      },
      {
        field: "validade",
        headerName: "Validade",
        width: 110,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => {
          const st = getValidadeStatus(params.row);
          const chip = validadeChipSx(st);
          return (
            <Chip
              size="small"
              label={chip.label}
              sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600 }}
            />
          );
        },
      },
      {
        field: "vinculos",
        headerName: "Vínculos",
        width: 100,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => {
          const n = countVinculos(params.row);
          return (
            <Tooltip
              title={
                isCupomGlobal(params.row)
                  ? "Válido em toda a loja"
                  : `${n} vínculo${n === 1 ? "" : "s"} (produtos, variações ou categorias)`
              }
            >
              <Chip
                size="small"
                label={isCupomGlobal(params.row) ? "Global" : n}
                variant={isCupomGlobal(params.row) ? "outlined" : "filled"}
                sx={
                  isCupomGlobal(params.row)
                    ? { fontWeight: 600 }
                    : {
                        bgcolor: "rgba(249, 96, 48, 0.12)",
                        color: CUPONS_LOJA_ACCENT,
                        fontWeight: 700,
                      }
                }
              />
            </Tooltip>
          );
        },
      },
      {
        field: "usoTotal",
        headerName: "Usos",
        width: 80,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "ativo",
        headerName: "Ativo",
        width: 90,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => (
          <Tooltip title={params.row.ativo ? "Desativar cupom" : "Ativar cupom"}>
            <Switch
              size="small"
              checked={Boolean(params.row.ativo)}
              disabled={togglingId === params.row.id}
              onChange={() => void toggleAtivo(params.row)}
              onClick={(e) => e.stopPropagation()}
              sx={switchSx}
            />
          </Tooltip>
        ),
      },
      {
        field: "acoes",
        headerName: "Ações",
        width: 100,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params: GridRenderCellParams<ICupomLoja>) => (
          <MDBox display="flex" justifyContent="center">
            <Tooltip title="Editar cupom">
              <IconButton size="small" onClick={() => openEdit(params.row)} sx={actionIconSx}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir cupom">
              <IconButton
                size="small"
                onClick={() =>
                  setOpenDelete({
                    open: true,
                    id: params.row.id,
                    codigo: params.row.codigo,
                  })
                }
                sx={{ color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
    ],
    [openEdit, toggleAtivo, togglingId],
  );

  return (
    <LojaPageContainer
      title="Cupons da loja"
      subtitle="Códigos promocionais com desconto em porcentagem ou valor fixo. Vincule a produtos, categorias ou deixe global."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={openCreate}
          sx={saveButtonSx}
        >
          Novo cupom
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "No filtro" : "Total cadastrados"}
            value={isFiltered ? filteredRows.length : stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Válidos agora" value={stats.validos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Ativos" value={stats.ativos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Usos registrados" value={stats.usos} />
        </Grid>
      </Grid>

      <MDBox sx={cuponsLojaToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ConfirmationNumberOutlinedIcon sx={{ color: CUPONS_LOJA_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {filteredRows.length} cupom{filteredRows.length === 1 ? "" : "s"}
              {isFiltered ? " no filtro" : " cadastrado" + (filteredRows.length === 1 ? "" : "s")}
            </MDTypography>
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Status
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={statusFilter}
              onChange={(_, next: StatusFilter | null) => {
                if (next === null) return;
                setStatusFilter(next);
              }}
              sx={statusToggleSx}
            >
              {STATUS_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value || "all-status"} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Validade
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={validadeFilter}
              onChange={(_, next: ValidadeFilter | null) => {
                if (next === null) return;
                setValidadeFilter(next);
              }}
              sx={statusToggleSx}
            >
              {VALIDADE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value || "all-validade"} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth sx={fieldSx}>
            <InputLabel shrink htmlFor="cupons-loja-search">
              Buscar
            </InputLabel>
            <OutlinedInput
              id="cupons-loja-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label="Buscar"
              notched
              placeholder="Código do cupom…"
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </FormControl>
        </Stack>
      </MDBox>

      {!loading && filteredRows.length === 0 ? (
        <MDBox sx={emptyStateSx}>
          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            {isFiltered ? "Nenhum cupom no filtro" : "Nenhum cupom cadastrado"}
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary" mb={2}>
            {isFiltered
              ? "Ajuste os filtros ou a busca por código."
              : "Crie cupons de desconto e vincule a produtos ou categorias."}
          </MDTypography>
          {!isFiltered ? (
            <MDButton
              variant="gradient"
              color="dark"
              startIcon={<AddOutlinedIcon />}
              onClick={openCreate}
              sx={saveButtonSx}
            >
              Criar primeiro cupom
            </MDButton>
          ) : null}
        </MDBox>
      ) : (
        <LojaDataGrid
          rows={filteredRows}
          getRowId={(r) => r.id}
          columns={columns}
          loading={loading}
          sx={gridSx}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
        />
      )}

      <CupomModal
        open={openForm}
        editing={editing}
        onClose={() => setOpenForm(false)}
        onSuccess={load}
      />

      <ConfirmDialog
        open={openDelete.open}
        title="Confirmar exclusão"
        message={`Excluir o cupom "${openDelete.codigo ?? ""}" permanentemente? Esta ação não pode ser desfeita.`}
        onClose={() => setOpenDelete({ open: false })}
        onConfirm={onConfirmDelete}
      />
    </LojaPageContainer>
  );
}
